import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material';
import atendimentoSetorService from '../../../services/atendimentoSetorService';
import { AtendimentoSetor, AtendimentoSubsetor } from '../../../types';

type SetorOption = AtendimentoSubsetor & {
  nivel?: number;
};

interface SetoresAllSelectProps {
  value: number | null;
  onChange: (setorId: number | null) => void;
  disabled?: boolean;
  label?: string;
  placeholder?: string;
}

const flattenSetores = (setores: AtendimentoSetor[]): SetorOption[] => {
  return setores.flatMap((setor) => {
    const subsetores = (setor.subsetores || []).map((subsetor) => ({
      ...subsetor,
      nivel: 1,
    }));

    return [{ ...setor, nivel: 0 }, ...subsetores];
  });
};

const SetoresAllSelect: React.FC<SetoresAllSelectProps> = ({
  value,
  onChange,
  disabled = false,
  label = 'Setor',
  placeholder = 'Selecione um setor',
}) => {
  const [setores, setSetores] = useState<SetorOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let mounted = true;

    const carregarSetores = async () => {
      try {
        setLoading(true);
        const response = await atendimentoSetorService.getAll();
        if (!mounted) return;
        setSetores(flattenSetores(response.data || []));
      } catch (error) {
        if (mounted) setSetores([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    carregarSetores();

    return () => {
      mounted = false;
    };
  }, []);

  const selectedOption = useMemo(
    () => setores.find((setor) => setor.id === value) || null,
    [setores, value]
  );

  return (
    <Autocomplete
      fullWidth
      size="small"
      disabled={disabled}
      loading={loading}
      options={setores}
      value={selectedOption}
      onChange={(_, option) => onChange(option?.id || null)}
      getOptionLabel={(option) => option.nome}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      renderOption={(props, option) => (
        <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: option.cor || 'primary.main',
              ml: option.nivel ? 2 : 0,
              flexShrink: 0,
            }}
          />
          <Typography variant="body2">{option.nome}</Typography>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default SetoresAllSelect;
